/**
 * BAM IQ's state in the browser: the conversation list, the open
 * conversation, the answer being written, what the coach has attached, and
 * proposed plan changes. One instance lives in BamIQProvider, above the page,
 * so the panel and the #/iq page share it and a conversation survives moving
 * between pages (UX proposal §E.3).
 *
 * Attachments (D122) are the coach's choice: added by a tap, shown as chips,
 * removable, sent as type and id only. Proposed plan changes (D123) show the
 * server's preview and the server's answer to Save, Not now and Undo; nothing
 * here decides that a change was saved.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchOverview, fetchThread, postAction, streamTurn, fetchPlayers, executeProposal, fetchCases } from "./api";

const STAGE_TEXT = {
  reading: "Reading your question",
  searching: "Searching BAM's library",
  thinking: "Thinking",
  researching: "Checking live sources",
  writing: "Writing",
};
export const stageText = (s) => STAGE_TEXT[s] || "Working";

const sameItem = (a, b) => a.type === b.type && String(a.id) === String(b.id);

export function useBamIQ({ enabled = true } = {}) {
  const [threads, setThreads] = useState([]);
  const [pins, setPins] = useState([]);                 // conversation ids pinned to the top
  const [usage, setUsage] = useState(null);             // { used: 0..1, resets } this month's allowance
  const [fields, setFields] = useState({});             // what BAM IQ can remember about the coach, by key
  const [founder, setFounder] = useState(false);        // the founder's tools (founder.js), as the server says
  const [cases, setCases] = useState(null);             // his test cases, loaded when opened
  const [memory, setMemory] = useState({ enabled: false, items: [] });
  const [threadId, setThreadId] = useState(null);
  const [threadPlayerId, setThreadPlayerId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(null);   // { coach, text, stage, emergency, notices, proposals, sentAt, ttftMs }
  const [error, setError] = useState(null);       // { text } a conversation that wouldn't load
  // A turn that didn't finish (Stop, or a failure): the question stays on
  // screen with what had been written, and Try again sends it again. The
  // server saved nothing for it. { coach, text, stopped, reason, retry }
  const [interrupted, setInterrupted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);   // [{ type, id, title }]
  const [players, setPlayers] = useState(null);         // the coach's own, loaded when needed
  const [playerConsent, setPlayerConsent] = useState(false);
  const [consentAsk, setConsentAsk] = useState(null);   // { retry } after the server asked for consent
  const abortRef = useRef(null);
  const partialRef = useRef("");

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const o = await fetchOverview();
      setThreads(o.threads || []);
      setPins(o.pins || []);
      setUsage(o.usage || null);
      setFields(o.fields || {});
      setFounder(o.founder === true);
      setMemory(o.memory || { enabled: false, items: [] });
      setPlayerConsent(!!o.player_consent);
    } catch { /* the list is a convenience; the conversation still works */ }
  }, [enabled]);
  useEffect(() => { refresh(); }, [refresh]);

  const loadPlayers = useCallback(async () => {
    try { const o = await fetchPlayers(); setPlayers(o.players || []); return o.players || []; } catch { setPlayers([]); return []; }
  }, []);

  const openThread = useCallback(async (id) => {
    abortRef.current?.abort();
    setPending(null); setError(null); setInterrupted(null); setConsentAsk(null);
    if (!id) { setThreadId(null); setThreadPlayerId(null); setMessages([]); setAttachments([]); return; }
    setLoading(true);
    try {
      const t = await fetchThread(id);
      setThreadId(id);
      setThreadPlayerId(t.thread_player_id || null);
      setMessages(t.messages || []);
      // Carry on with what was attached last, so a plan conversation stays a plan conversation.
      const lastCoach = [...(t.messages || [])].reverse().find(m => m.role === "coach");
      const carried = (lastCoach?.attachments || []).map(a => ({ type: a.type, id: a.id, title: a.title || null }));
      setAttachments(carried);
      if (carried.some(a => a.type === "player" && !a.title)) {
        const list = await loadPlayers();
        setAttachments(cur => cur.map(a => (a.type === "player" && !a.title ? { ...a, title: list.find(p => p.id === a.id)?.name || "Player" } : a)));
      }
    } catch (e) {
      setError({ text: e.message });
    } finally {
      setLoading(false);
    }
  }, [loadPlayers]);

  /** The coach's tap on "attach". A player's plan starts its own conversation. */
  const attach = useCallback((item) => {
    if (!item?.type || !item.id) return;
    if (item.type === "player" && threadId && threadPlayerId !== item.id) {
      openThread(null);
      setAttachments([item]);
      return;
    }
    setAttachments(cur => {
      if (cur.some(a => sameItem(a, item))) return cur;
      const rest = item.type === "player" ? cur.filter(a => a.type !== "player") : cur;
      return [...rest, item].slice(-3);
    });
  }, [threadId, threadPlayerId, openThread]);
  const detach = useCallback((item) => setAttachments(cur => cur.filter(a => !sameItem(a, item))), []);

  // `fresh`: a new conversation for this question (the orb's quick ask), whatever was open.
  const send = useCallback(async (text, { historyOff = false, fresh = false } = {}) => {
    const message = String(text || "").trim();
    if (!message || pending) return;
    setError(null); setInterrupted(null); setConsentAsk(null);
    const tid = fresh ? null : threadId;
    if (fresh) { setThreadId(null); setThreadPlayerId(null); setMessages([]); setAttachments([]); }
    partialRef.current = "";
    const sentAt = performance.now();
    const controller = new AbortController();
    abortRef.current = controller;
    const attached = fresh ? [] : attachments;
    setPending({ coach: message, attachments: attached, text: "", stage: "reading", emergency: null, notices: [], proposals: [], sources: [], sentAt, ttftMs: null });
    try {
      const done = await streamTurn({ threadId: tid, message, historyOff, attachments: attached }, (ev) => {
        setPending(p => {
          if (!p) return p;
          if (ev.type === "stage") return { ...p, stage: ev.stage };
          if (ev.type === "sources") return { ...p, sources: Array.isArray(ev.sources) ? ev.sources : [] };
          if (ev.type === "emergency") return { ...p, emergency: ev.text };
          if (ev.type === "notice") return { ...p, notices: [...p.notices, ev.text] };
          if (ev.type === "proposal") return { ...p, proposals: [...p.proposals, { ...ev.proposal, status: "proposed" }] };
          if (ev.type === "text") { partialRef.current = p.text + ev.text; return { ...p, text: p.text + ev.text, ttftMs: p.ttftMs ?? Math.round(performance.now() - sentAt) }; }
          return p;
        });
      }, controller.signal);
      const now = new Date().toISOString();
      setThreadId(done.thread_id);
      if (attached.some(a => a.type === "player")) setThreadPlayerId(attached.find(a => a.type === "player").id);
      setMessages(m => [...m,
        { id: `c-${now}`, role: "coach", body: message, attachments: attached, created_at: now },
        // fresh: it carries on from where the stream was on screen (smoothText.js), instead of jumping to the end.
        { id: done.message.id || `a-${now}`, role: "assistant", fresh: true, body: done.message.body, refs: done.message.refs || [],
          emergency: done.message.emergency, notices: done.message.notices || [], remember: done.message.remember,
          proposals: (done.message.proposals || []).map(p => ({ ...p, status: "proposed" })),
          profile_used: !!done.context_used?.profile, feedback: null,
          timing: done.context_used?.timing, created_at: now }]);
      setPending(null);
      refresh();
    } catch (e) {
      setPending(null);
      if (e.code === "consent_required") { setConsentAsk({ coach: message, attachments: attached, retry: () => send(message, { historyOff, fresh }) }); return; }
      if (e.code === "monthly_limit") setUsage(u => ({ ...(u || {}), used: 1 }));
      const stopped = e.name === "AbortError";
      setInterrupted({ coach: message, text: partialRef.current, stopped,
        reason: stopped ? "Stopped." : (e.message || "Something went wrong."),
        // A refusal (not signed in, no access, not in the app) won't change on a retry.
        retry: e.status === 401 || e.status === 403 || e.code === "monthly_limit" ? null : () => send(message, { historyOff, fresh }) });
    } finally {
      abortRef.current = null;
    }
  }, [threadId, pending, refresh, attachments]);

  const stop = useCallback(() => { abortRef.current?.abort(); }, []);

  /** The coach's own answer to the consent card. */
  const answerConsent = useCallback(async (granted) => {
    const ask = consentAsk;
    setConsentAsk(null);
    if (!granted) { setAttachments(cur => cur.filter(a => a.type !== "player")); return; }
    await postAction({ action: "player_consent", granted: true });
    setPlayerConsent(true);
    ask?.retry?.();
  }, [consentAsk]);

  /** Save, Not now or Undo on a proposal: the server decides, and its answer is shown. */
  const decide = useCallback(async (messageId, proposal, kind) => {
    const setStatus = (status, extra = {}) => setMessages(ms => ms.map(m => (m.id !== messageId ? m : {
      ...m, proposals: (m.proposals || []).map(p => (p.action_id === proposal.action_id ? { ...p, status, ...extra } : p)) })));
    setStatus(proposal.status, { busy: kind });
    let out;
    try {
      out = await executeProposal({ action_id: proposal.action_id, kind, preview_hash: proposal.preview_hash });
    } catch {
      out = { status: "failed" };
    }
    setStatus(out.status || "failed", { busy: null });
    if (out.status === "saved" || out.status === "undone") {
      // Any open Dev Studio reloads this player (its existing listener).
      try { window.dispatchEvent(new CustomEvent("bam:players-changed", { detail: { player_id: proposal.player_id } })); } catch { /* ignore */ }
    }
    return out;
  }, []);

  const remember = useCallback(async (messageId, suggestion) => {
    await postAction({ action: "remember", key: suggestion.key, value: suggestion.value });
    setMessages(m => m.map(x => (x.id === messageId ? { ...x, remember: null, remembered: suggestion } : x)));
    refresh();
  }, [refresh]);
  const dismissRemember = useCallback((messageId) => {
    setMessages(m => m.map(x => (x.id === messageId ? { ...x, remember: null } : x)));
  }, []);
  const setMemoryOn = useCallback(async (on) => {
    setMemory(m => ({ ...m, enabled: !!on }));
    try { await postAction({ action: "memory", enabled: !!on }); } finally { refresh(); }
  }, [refresh]);
  const forget = useCallback(async (key) => { await postAction({ action: "forget", key }); refresh(); }, [refresh]);
  /** The coach's own edit on "What BAM IQ knows about you". The server checks the value. */
  const setMemoryItem = useCallback(async (key, value) => {
    try { await postAction({ action: "remember", key, value }); return null; } catch (e) { return e.message; } finally { refresh(); }
  }, [refresh]);
  /** The founder's feedback on an answer; `rating` null takes it back. The server keeps what produced it. */
  const feedback = useCallback(async (messageId, rating, reasons = [], note = null) => {
    const prev = messages.find(m => m.id === messageId)?.feedback || null;
    const next = rating ? { rating, ...(reasons.length ? { reasons } : {}), ...(note ? { note } : {}) } : null;
    setMessages(ms => ms.map(m => (m.id === messageId ? { ...m, feedback: next } : m)));
    try { await postAction({ action: "feedback", message_id: messageId, rating, reasons, ...(note ? { note } : {}) }); return true; }
    catch { setMessages(ms => ms.map(m => (m.id === messageId ? { ...m, feedback: prev } : m))); return false; }
  }, [messages]);
  // His test cases: made from a rated answer, reviewed, rerun, compared.
  const loadCases = useCallback(async () => { try { const o = await fetchCases(); setCases(o.cases || []); } catch { setCases([]); } }, []);
  const caseAction = useCallback(async (body) => {
    const o = await postAction(body);
    if (o.case) setCases(cur => [o.case, ...(cur || []).filter(c => c.id !== o.case.id)]);
    if (o.deleted) setCases(cur => (cur || []).filter(c => c.id !== o.deleted));
    return o;
  }, []);
  const makeCase = useCallback((messageId) => caseAction({ action: "case_create", message_id: messageId }), [caseAction]);
  const updateCase = useCallback((id, patch) => caseAction({ action: "case_update", id, ...patch }), [caseAction]);
  const deleteCase = useCallback((id) => caseAction({ action: "case_delete", id }), [caseAction]);
  const rerunCase = useCallback((id) => caseAction({ action: "case_rerun", id }), [caseAction]);
  // Reviewed cases for the analysis, masked again on the server before they leave the platform (2026-10-25).
  const exportCases = useCallback(async (ids = null) => (await postAction({ action: "cases_export", ...(ids ? { ids } : {}) })).bundle, []);
  const pin = useCallback(async (id, pinned) => {
    setPins(cur => (pinned ? [id, ...cur.filter(x => x !== id)] : cur.filter(x => x !== id)));
    try { const o = await postAction({ action: "pin", thread_id: id, pinned: !!pinned }); setPins(o.pins || []); } catch { refresh(); }
  }, [refresh]);
  const removeThread = useCallback(async (id) => {
    await postAction({ action: "delete_thread", thread_id: id });
    if (id === threadId) { setThreadId(null); setThreadPlayerId(null); setMessages([]); setAttachments([]); }
    refresh();
  }, [threadId, refresh]);

  return { threads, pins, pin, usage, fields, setMemoryItem, feedback, founder, cases, loadCases, makeCase, updateCase, deleteCase, rerunCase, exportCases, memory, threadId, threadPlayerId, messages, pending, error, interrupted, loading,
    attachments, attach, detach, players, loadPlayers, playerConsent, consentAsk, answerConsent, decide,
    openThread, newThread: () => openThread(null), send, stop, remember, dismissRemember, setMemoryOn, forget, removeThread, refresh };
}
